package one.devsbox.www.ui.widgets

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupProperties
import com.godaddy.android.colorpicker.ClassicColorPicker
import com.godaddy.android.colorpicker.HsvColor

@Composable
fun ColorFieldPickerDialog(
    label: String,
    value: androidx.compose.ui.text.input.TextFieldValue,
    color: Color,
    onValueChange: (androidx.compose.ui.text.input.TextFieldValue) -> Unit
) {
    var showPickerDialog by remember { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        LabelField(text = label)
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            OutlinedTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.weight(1f),
                shape = RoundedCornerShape(4.dp),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = MaterialTheme.colorScheme.primary,
                    unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                )
            )
            Box(
                modifier = Modifier
                    .size(54.dp)
                    .clip(RoundedCornerShape(4.dp))
                    .background(color)
                    .border(
                        1.dp,
                        MaterialTheme.colorScheme.outlineVariant,
                        RoundedCornerShape(4.dp)
                    )
                    .clickable {
                        showPickerDialog = true
                    }
            )
        }
    }

    if (showPickerDialog) {
        ColorPickerDialog(
            visible = showPickerDialog,
            onDismissRequest = { showPickerDialog = false },
            initialColor = color,
            label = label,
            onColorPicked = { pickedColor ->
                val red = (pickedColor.red * 255).toInt().toString(16).padStart(2, '0')
                val green = (pickedColor.green * 255).toInt().toString(16).padStart(2, '0')
                val blue = (pickedColor.blue * 255).toInt().toString(16).padStart(2, '0')
                val hex = "#FF${red.uppercase()}${green.uppercase()}${blue.uppercase()}"
                onValueChange(androidx.compose.ui.text.input.TextFieldValue(hex))
            }
        )
    }
}

@Composable
fun ColorPickerDialog(
    visible: Boolean,
    onDismissRequest: () -> Unit,
    initialColor: Color,
    label: String,
    onColorPicked: (Color) -> Unit
) {
    if (visible) {
        var lastColor by remember(initialColor) { mutableStateOf(HsvColor.from(initialColor)) }

        AlertDialog(
            onDismissRequest = onDismissRequest,
            title = {
                Text(
                    text = label,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
            },
            text = {
                Box(
                    modifier = Modifier.fillMaxWidth().height(260.dp),
                    contentAlignment = Alignment.Center
                ) {
                    ClassicColorPicker(
                        modifier = Modifier.size(250.dp),
                        color = lastColor,
                        showAlphaBar = false,
                        onColorChanged = { color: HsvColor ->
                            lastColor = color
                        }
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        onColorPicked(lastColor.toColor())
                        onDismissRequest()
                    }
                ) {
                    Text("OK")
                }
            },
            dismissButton = {
                TextButton(onClick = onDismissRequest) {
                    Text("CANCEL")
                }
            }
        )
    }
}

@Composable
fun ColorPickerPopup(
    visible: Boolean,
    onDismissRequest: () -> Unit,
    initialColor: Color,
    onColorPicked: (Color) -> Unit,
    modifier: Modifier = Modifier
) {
    if (visible) {
        var lastColor by remember(initialColor) { mutableStateOf(HsvColor.from(initialColor)) }

        Popup(
            onDismissRequest = onDismissRequest,
            properties = PopupProperties(
                focusable = true,
                dismissOnClickOutside = true,
                dismissOnBackPress = true
            )
        ) {
            Surface(
                shape = RoundedCornerShape(8.dp),
                color = MaterialTheme.colorScheme.surface,
                tonalElevation = 6.dp,
                modifier = modifier
                    .width(280.dp)
                    .padding(8.dp)
                    .border(
                        1.dp,
                        MaterialTheme.colorScheme.outlineVariant,
                        RoundedCornerShape(8.dp)
                    )
            ) {
                Column(
                    modifier = Modifier.padding(8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    ClassicColorPicker(
                        modifier = Modifier.size(220.dp),
                        color = lastColor,
                        showAlphaBar = false,
                        onColorChanged = { color: HsvColor ->
                            lastColor = color
                        }
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        TextButton(onClick = onDismissRequest) {
                            Text("CANCEL")
                        }
                        Spacer(modifier = Modifier.width(4.dp))
                        Button(
                            onClick = {
                                onColorPicked(lastColor.toColor())
                                onDismissRequest()
                            }
                        ) {
                            Text("OK")
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ColorFieldPickerPopup(
    label: String,
    value: androidx.compose.ui.text.input.TextFieldValue,
    color: Color,
    onValueChange: (androidx.compose.ui.text.input.TextFieldValue) -> Unit
) {
    var showPickerDialog by remember { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        LabelField(text = label)
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            OutlinedTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.weight(1f),
                shape = RoundedCornerShape(4.dp),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = MaterialTheme.colorScheme.primary,
                    unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
                )
            )
            Box(
                modifier = Modifier
                    .size(54.dp)
                    .clip(RoundedCornerShape(4.dp))
                    .background(color)
                    .border(
                        1.dp,
                        MaterialTheme.colorScheme.outlineVariant,
                        RoundedCornerShape(4.dp)
                    )
                    .clickable {
                        showPickerDialog = true
                    }
            ) {
                if (showPickerDialog) {
                    ColorPickerPopup(
                        visible = showPickerDialog,
                        onDismissRequest = { showPickerDialog = false },
                        initialColor = color,
                        onColorPicked = { pickedColor ->
                            val red = (pickedColor.red * 255).toInt().toString(16).padStart(2, '0')
                            val green =
                                (pickedColor.green * 255).toInt().toString(16).padStart(2, '0')
                            val blue =
                                (pickedColor.blue * 255).toInt().toString(16).padStart(2, '0')
                            val hex = "#FF${red.uppercase()}${green.uppercase()}${blue.uppercase()}"
                            onValueChange(androidx.compose.ui.text.input.TextFieldValue(hex))
                        }
                    )
                }
            }
        }


    }

}
